import React from 'react';
import { Info, PiggyBank, Calculator, TrendingDown, AlertCircle, CreditCard, Scale, FileText } from 'lucide-react';

/**
 * Компонент с информацией о досрочном погашении кредитов и полезными сведениями
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о досрочном погашении кредитов
      </h2>
      
      <div className="prose prose-green dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Что такое досрочное погашение кредита?
        </h3>
        <p>
          <strong>Досрочное погашение кредита</strong> — это внесение суммы, превышающей размер обязательного ежемесячного платежа. Это может быть как полное закрытие кредита раньше срока, так и частичное погашение, направленное на уменьшение долговой нагрузки и экономию на процентах.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-500" />
          Варианты досрочного погашения
        </h3>
        <ul>
          <li>
            <strong>Уменьшение срока кредита</strong> — размер ежемесячного платежа остается прежним, но сокращается количество оставшихся платежей. Этот вариант позволяет максимально сэкономить на процентах.
          </li>
          <li>
            <strong>Уменьшение платежа</strong> — срок кредита остается прежним, но снижается размер ежемесячных платежей. Этот вариант помогает уменьшить финансовую нагрузку на бюджет.
          </li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Экономия на процентах
          </h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            Благодаря особенностям аннуитетных платежей, в начале срока кредита большую часть платежа составляют проценты. 
            Поэтому досрочное погашение наиболее выгодно производить в первой трети срока кредита — 
            это дает максимальную экономию на переплате.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-green-500" />
          Законодательное регулирование в России
        </h3>
        <p>
          В России право заемщика на досрочное погашение кредита закреплено на законодательном уровне в Федеральном законе №353-ФЗ "О потребительском кредите (займе)" и в статье 810 Гражданского кодекса РФ.
        </p>
        <ul>
          <li>
            <strong>Ипотечные кредиты:</strong> Заемщик вправе досрочно погасить ипотеку в любой момент, уведомив кредитора не менее чем за 30 дней до даты погашения.
          </li>
          <li>
            <strong>Потребительские кредиты:</strong> Для погашения необходимо уведомить банк за 30 дней, но в договоре может быть указан и меньший срок (обычно от 3 до 14 дней).
          </li>
          <li>
            <strong>Комиссии:</strong> С 2011 года в России запрещено взимать комиссии и штрафы за досрочное погашение кредитов, за некоторыми исключениями для потребительских кредитов в первые 30 дней.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Важно знать при досрочном погашении
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно читайте кредитный договор — в нем указаны условия и порядок досрочного погашения</li>
            <li>Уточните в банке минимальную сумму досрочного погашения (обычно от 10 000 до 15 000 ₽)</li>
            <li>Многие банки принимают досрочное погашение только в дату ежемесячного платежа</li>
            <li>Обязательно запросите обновленный график платежей после досрочного погашения</li>
            <li>При полном погашении уточните в банке точную сумму закрытия с учетом начисленных процентов</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-500" />
          Процедура досрочного погашения
        </h3>
        <ol>
          <li>
            <strong>Уведомление банка:</strong> Написать заявление на досрочное погашение (в отделении, через интернет-банк или мобильное приложение).
          </li>
          <li>
            <strong>Выбор варианта:</strong> Указать в заявлении, что вы хотите уменьшить — срок кредита или размер платежа.
          </li>
          <li>
            <strong>Внесение денег:</strong> Обеспечить наличие необходимой суммы на счете в указанную дату.
          </li>
          <li>
            <strong>Получение документов:</strong> Запросить обновленный график платежей и справку об остатке задолженности.
          </li>
        </ol>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-500" />
          Специфика досрочного погашения разных типов кредитов
        </h3>
        <h4>Ипотека</h4>
        <p>
          Досрочное погашение ипотеки особенно выгодно из-за больших сумм и длительных сроков. 
          Многие банки предоставляют онлайн-калькуляторы для расчета выгоды. 
          При использовании материнского капитала для погашения ипотеки обязательно укажите это в заявлении.
        </p>

        <h4>Потребительские кредиты</h4>
        <p>
          Для небольших потребительских кредитов предпочтительнее схема с уменьшением срока, 
          так как экономия на процентах будет более заметной, чем снижение ежемесячного платежа.
        </p>

        <h4>Автокредиты</h4>
        <p>
          При досрочном погашении автокредита стоит учитывать наличие страховок (КАСКО, ОСАГО), 
          которые могут быть включены в сумму кредита. При полном погашении вы можете подать заявление 
          на возврат части страховой премии за неиспользованный период.
        </p>

        <h3>Расчет оптимальной стратегии досрочного погашения</h3>
        <p>
          Для выбора наилучшей стратегии досрочного погашения следует учитывать:
        </p>
        <ul>
          <li>Тип кредита и процентную ставку</li>
          <li>Оставшийся срок погашения</li>
          <li>Ваши финансовые возможности и регулярность дополнительных платежей</li>
          <li>Текущую финансовую нагрузку на бюджет</li>
          <li>Возможности реинвестирования средств с более высокой доходностью</li>
        </ul>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Пример расчета эффективности
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
            Для кредита в 1 млн рублей на 5 лет под 12% годовых, досрочное погашение 200 000 рублей через год:
          </p>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li>При уменьшении срока: экономия составит около 76 000 рублей</li>
            <li>При уменьшении платежа: экономия составит около 60 000 рублей</li>
            <li>Эффективность инвестиции: 30-38% годовых</li>
          </ul>
        </div>

        <h3>Альтернативы досрочному погашению</h3>
        <p>
          В некоторых случаях полное досрочное погашение может быть не самым выгодным решением:
        </p>
        <ul>
          <li>
            <strong>Рефинансирование</strong> — если рыночные ставки снизились, можно рефинансировать кредит под более низкий процент.
          </li>
          <li>
            <strong>Инвестирование</strong> — при низкой ставке по кредиту (ниже 8-9% годовых) средства может быть выгоднее направить в консервативные инвестиции.
          </li>
          <li>
            <strong>Формирование финансовой подушки</strong> — создание резервного фонда на 3-6 месяцев расходов может быть приоритетнее досрочного погашения.
          </li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам оценить эффективность досрочного погашения кредита, 
          сравнить различные стратегии и принять оптимальное финансовое решение!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;